export default defineNuxtPlugin(() => {
    if (!import.meta.client) return

    // Locale-based installation switching is disabled for now — only 'de' is used.
    // const { locale } = useI18n()
    const config = useRuntimeConfig().public

    // const installationIds: Record<string, string> = {
    //     de: config.doofinderInstallationIdDe,
    //     en: config.doofinderInstallationIdEn,
    // }
    // const installationId = installationIds[locale.value]
    const installationId = config.doofinderInstallationIdDe

    if (!installationId) return
    if (document.getElementById('doofinder-search-layer')) return

    const script = document.createElement('script')
    script.id = 'doofinder-search-layer'
    script.src = `https://eu1-config.doofinder.com/2.x/${installationId}.js`
    script.async = true
    document.head.appendChild(script)

    // Doofinder's own "Rasterize Image" plugin setting only appends `?width=`
    // to the original CDN URL, which our CDN doesn't resolve, so full-size
    // originals get served in the search layer. Rewrite them through the
    // same imgproxy used by the storefront instead.
    const imgProxyUrl = config.imgProxyUrl as string

    const urlSafeBase64 = (value: string) => btoa(unescape(encodeURIComponent(value)))
        .replace(/=+$/, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')

    // Sized for the Doofinder card's at most ~220x220 thumbnail box at roughly 2x for
    // retina sharpness
    const proxiedImageUrl = (src: string) => {
        const encoded = urlSafeBase64(src.split('?')[0] ?? src)
        return `${imgProxyUrl}/_/rt:fit/w:440/h:440/ex:1/t:1/${encoded}`
    }

    const proxyImage = (img: HTMLImageElement) => {
        if (!imgProxyUrl || img.dataset.imgProxied) return
        img.dataset.imgProxied = 'true'
        img.src = proxiedImageUrl(img.src)
    }

    // Doofinder's feed links point to the Shopware storefront domain. Keep
    // path, query and hash but swap the origin for the PWA's own, so users
    // stay on the headless frontend. `dfd-value-link` is what Doofinder's
    // own click handler navigates to, so it has to be rewritten as well.
    const linkSelector = '.dfd-card a[href], .dfd-card[dfd-value-link]'

    const linkOrigin = window.location.origin

    // juppen/prange share the gisy API domain but their sales channels are
    // bound under a `/juppen` or `/prange` path prefix in Shopware (gisy, as
    // the default sales channel, has none). Doofinder indexes that prefix as
    // part of the SEO URL, but the headless storefront for each brand lives
    // at its own domain root without it, so it has to be stripped here too.
    const theme = config.theme as string
    const salesChannelPathPrefix = theme && theme !== 'gisy' ? `/${theme}` : null

    const toLocalUrl = (value: string) => {
        try {
            const url = new URL(value, window.location.origin)
            let pathname = url.pathname
            if (salesChannelPathPrefix && (pathname === salesChannelPathPrefix || pathname.startsWith(`${salesChannelPathPrefix}/`))) {
                pathname = pathname.slice(salesChannelPathPrefix.length) || '/'
            }
            if (url.origin === linkOrigin && pathname === url.pathname) return value
            return `${linkOrigin}${pathname}${url.search}${url.hash}`
        } catch {
            return value
        }
    }

    const rewriteLink = (el: Element) => {
        for (const attr of ['href', 'dfd-value-link']) {
            const value = el.getAttribute(attr)
            if (!value) continue
            const localUrl = toLocalUrl(value)
            if (localUrl !== value) el.setAttribute(attr, localUrl)
        }
    }

    const rewriteDoofinderCards = (el: Element) => {
        if (el.matches('.dfd-card-thumbnail img[src]')) proxyImage(el as HTMLImageElement)
        el.querySelectorAll<HTMLImageElement>('.dfd-card-thumbnail img[src]').forEach(proxyImage)

        if (el.matches(linkSelector)) rewriteLink(el)
        el.querySelectorAll(linkSelector).forEach(rewriteLink)
    }

    // Doofinder mounts a single `.dfd-root` element directly on <body> and
    // re-renders everything inside it (results, pagination, filtering)
    // without recreating that root. So we only need a broad, page-wide
    // observer once, to catch that root appearing; after that we scope
    // down to just its subtree instead of watching the whole page forever.
    const watchDoofinderRoot = (root: Element) => {
        rewriteDoofinderCards(root)

        const rootObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== Node.ELEMENT_NODE) return
                    rewriteDoofinderCards(node as Element)
                })
            }
        })

        rootObserver.observe(root, { childList: true, subtree: true })
    }

    const existingRoot = document.querySelector('.dfd-root')
    if (existingRoot) {
        watchDoofinderRoot(existingRoot)
    } else {
        const bodyObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue
                    const el = node as Element
                    const root = el.matches('.dfd-root') ? el : el.querySelector('.dfd-root')
                    if (root) {
                        bodyObserver.disconnect()
                        watchDoofinderRoot(root)
                        return
                    }
                }
            }
        })

        bodyObserver.observe(document.body, { childList: true, subtree: true })
    }
})
